/**
 * Biometric unlock with WebAuthn PRF (Phase 16D D2, R-308, design §6.2).
 *
 * A platform passkey with the `prf` extension (eval.first = SHA-256("pelora-offline-v1")) gives a
 * stable 32-byte secret after Face ID / fingerprint. It is offered only when it gives the same
 * output twice (self-test). PRF output → HKDF-SHA256 → AES-KW wraps a SECOND copy of the DEK in
 * `meta`; the PIN stays the fallback and its 5-attempt rule is unchanged. A failed biometric never
 * counts as a wrong PIN. Where PRF is missing (older iOS, some browsers): "Not available on this device".
 */
import { randomBytes, sha256, toBase64, fromBase64 } from './crypto';

export const PRF_LABEL = 'pelora-offline-v1';
export const PRF_HKDF_INFO = 'pelora-offline-prf-v1';
export const BIOMETRIC_UNAVAILABLE = 'Not available on this device';

type PrfResults = { prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } } };

export class BiometricError extends Error {
  constructor(message: string, readonly code: 'UNAVAILABLE' | 'CANCELLED' | 'MISMATCH' | 'MISSING') {
    super(message);
    this.name = 'BiometricError';
  }
}

function credentialsApi(): CredentialsContainer | null {
  return typeof navigator !== 'undefined' && navigator.credentials && typeof window !== 'undefined' && 'PublicKeyCredential' in window ? navigator.credentials : null;
}

/** A platform authenticator with user verification exists (PRF itself is only known after create). */
export async function biometricAvailable(): Promise<boolean> {
  if (!credentialsApi()) return false;
  const pkc = (window as unknown as { PublicKeyCredential?: { isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean> } }).PublicKeyCredential;
  try {
    return Boolean(await pkc?.isUserVerifyingPlatformAuthenticatorAvailable?.());
  } catch {
    return false;
  }
}

const cancelled = (e: unknown) => e instanceof Error && (e.name === 'NotAllowedError' || e.name === 'AbortError');

/** Evaluate the PRF with an existing credential. */
export async function evaluatePrf(credentialId: string): Promise<ArrayBuffer> {
  const api = credentialsApi();
  if (!api) throw new BiometricError(BIOMETRIC_UNAVAILABLE, 'UNAVAILABLE');
  let assertion: Credential | null;
  try {
    assertion = await api.get({
      publicKey: {
        challenge: randomBytes(32),
        allowCredentials: [{ type: 'public-key', id: fromBase64(credentialId) }],
        userVerification: 'required',
        timeout: 60_000,
        extensions: { prf: { eval: { first: await sha256(PRF_LABEL) } } } as AuthenticationExtensionsClientInputs,
      },
    });
  } catch (e) {
    if (cancelled(e)) throw new BiometricError('Fingerprint or face unlock was cancelled. Use your PIN.', 'CANCELLED');
    throw new BiometricError('Fingerprint or face unlock is not set up on this device any more. Use your PIN, then set it up again.', 'MISSING');
  }
  const out = (assertion as PublicKeyCredential | null)?.getClientExtensionResults() as PrfResults | undefined;
  const first = out?.prf?.results?.first;
  if (!first) throw new BiometricError(BIOMETRIC_UNAVAILABLE, 'UNAVAILABLE');
  return first;
}

const equalBytes = (a: ArrayBuffer, b: ArrayBuffer) => {
  const x = new Uint8Array(a);
  const y = new Uint8Array(b);
  return x.length === y.length && x.every((v, i) => v === y[i]);
};

/**
 * Create a platform passkey with PRF and self-test it: two evaluations must agree before the
 * device is offered biometric unlock. Returns the credential id and the PRF secret.
 */
export async function enrollBiometric(userHandle: string, label: string): Promise<{ credentialId: string; secret: ArrayBuffer }> {
  const api = credentialsApi();
  if (!api || !(await biometricAvailable())) throw new BiometricError(BIOMETRIC_UNAVAILABLE, 'UNAVAILABLE');
  let created: Credential | null;
  try {
    created = await api.create({
      publicKey: {
        rp: { name: 'pelora' },
        user: { id: (await sha256(userHandle)).slice(0, 16), name: label, displayName: label },
        challenge: randomBytes(32),
        pubKeyCredParams: [{ type: 'public-key', alg: -7 }, { type: 'public-key', alg: -257 }],
        authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required', residentKey: 'preferred' },
        timeout: 60_000,
        extensions: { prf: { eval: { first: await sha256(PRF_LABEL) } } } as AuthenticationExtensionsClientInputs,
      },
    });
  } catch (e) {
    if (cancelled(e)) throw new BiometricError('Set-up was cancelled.', 'CANCELLED');
    throw new BiometricError(BIOMETRIC_UNAVAILABLE, 'UNAVAILABLE');
  }
  const credential = created as PublicKeyCredential | null;
  const ext = credential?.getClientExtensionResults() as PrfResults | undefined;
  if (!credential || ext?.prf?.enabled === false) throw new BiometricError(BIOMETRIC_UNAVAILABLE, 'UNAVAILABLE');
  const credentialId = toBase64(credential.rawId);
  const first = await evaluatePrf(credentialId);
  const second = await evaluatePrf(credentialId);
  if (!equalBytes(first, second)) throw new BiometricError(BIOMETRIC_UNAVAILABLE, 'MISMATCH');
  return { credentialId, secret: first };
}
